/**
 * TYPES: FORMS
 * Types pour la feature de formulaires personnalisables
 */

import type { Contact } from './contact';
import type { Product } from './product';

// =====================================================
// ENUMS
// =====================================================

/**
 * Types de champs disponibles dans les formulaires
 */
export type FormFieldType =
  | 'text' // Texte court
  | 'textarea' // Texte long
  | 'email' // Email (validation automatique)
  | 'phone' // Téléphone
  | 'number' // Nombre
  | 'date' // Date
  | 'select' // Liste déroulante
  | 'checkbox' // Case à cocher
  | 'radio'; // Bouton radio

/**
 * Labels lisibles pour les types de champs
 */
export const FORM_FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  text: 'Texte court',
  textarea: 'Texte long',
  email: 'Email',
  phone: 'Téléphone',
  number: 'Nombre',
  date: 'Date',
  select: 'Liste déroulante',
  checkbox: 'Case à cocher',
  radio: 'Bouton radio',
};

/**
 * Type de formulaire
 * - simple : 1 prospect par soumission (défaut)
 * - duo : pilote + copilote (2 prospects si copilote renseigné)
 */
export type FormType = 'simple' | 'duo';

/**
 * Labels lisibles pour les types de formulaire
 */
export const FORM_TYPE_LABELS: Record<FormType, string> = {
  simple: 'Simple',
  duo: 'Duo (Pilote + Copilote)',
};

/**
 * Rôle cible pour les champs de formulaires duo
 * - pilot : champ destiné au pilote
 * - copilot : champ destiné au copilote
 * - null : champ commun (formulaires simples ou partagé)
 */
export type TargetRole = 'pilot' | 'copilot' | null;

/**
 * Groupes d'informations critiques du BED que le participant peut reporter
 * (« je m'engage à communiquer avant le … », cahier des charges L14).
 * Miroir exact de la contrainte SQL `field_templates.bed_deferrable_group`
 * (00177). Le certificat médical partage la même mécanique de report mais
 * n'est pas un champ de formulaire (c'est un upload) : il vit dans
 * `BedDeferralGroup` côté wizard, pas ici.
 */
export type BedDeferrableGroup = 'passport' | 'permis' | 'ceam';

/**
 * Labels lisibles pour les rôles cibles
 */
export const TARGET_ROLE_LABELS: Record<string, string> = {
  pilot: 'Pilote',
  copilot: 'Copilote',
};

// =====================================================
// INTERFACES
// =====================================================

/**
 * Template de champ réutilisable
 */
export interface FieldTemplate {
  id: string;
  name: string; // Nom unique
  label: string;
  field_type: FormFieldType;
  placeholder?: string;
  is_required: boolean;
  options?: string[];
  contact_field_map?: string; // Champ de contact à mapper (ex: 'first_name', 'email')
  category: string; // Ex: 'contact', 'address', 'default'
  is_system: boolean; // Template système (non modifiable/supprimable)
  target_role?: TargetRole; // Rôle cible pour les formulaires duo
  /**
   * BED (00177) — groupe d'informations critiques reportable sur le bulletin.
   * Non nul, le wizard propose « je ne connais pas encore cette info — je
   * m'engage à la communiquer avant le [date] » pour tout le groupe.
   */
  bed_deferrable_group?: BedDeferrableGroup | null;
  created_at: string;
  updated_at: string;
}

export interface CreateFieldTemplateDto {
  name: string;
  label: string;
  field_type: FormFieldType;
  placeholder?: string;
  is_required?: boolean;
  options?: string[];
  contact_field_map?: string;
  category?: string;
  is_system?: boolean;
}

/**
 * Labels pour les catégories de templates
 */
export const FIELD_TEMPLATE_CATEGORY_LABELS: Record<string, string> = {
  contact: 'Contact',
  address: 'Adresse',
  default: 'Autres',
};

/**
 * Association champ-template (avec overrides optionnels)
 */
export interface FormField {
  id: string;
  form_id: string;
  field_template_id: string;
  order_index: number;

  // Overrides optionnels (NULL = utilise le template)
  label?: string; // Override du label
  placeholder?: string; // Override du placeholder
  is_required?: boolean; // Override du caractère requis
  options?: string[]; // Override des options

  created_at: string;
  updated_at: string;

  // Relation (chargée via Supabase select)
  field_template?: FieldTemplate;
}

/**
 * FormField avec valeurs effectives (template + overrides fusionnés)
 * Utilisé pour l'affichage
 */
export interface FormFieldWithValues {
  id: string;
  form_id: string;
  field_template_id: string;
  order_index: number;

  // Valeurs effectives (template OU override)
  label: string;
  field_type: FormFieldType;
  placeholder?: string;
  is_required: boolean;
  options?: string[];
  contact_field_map?: string;
  target_role?: TargetRole; // Rôle cible pour les formulaires duo
  /** BED (00177) — cf. FieldTemplate.bed_deferrable_group. */
  bed_deferrable_group?: BedDeferrableGroup | null;

  created_at: string;
  updated_at: string;
}

/**
 * Formulaire complet
 */
export interface Form {
  id: string;
  name: string;
  slug: string;
  product_id?: string;
  origin_id?: string;
  is_active: boolean;
  form_type: FormType; // Type de formulaire (simple ou duo)
  generates_bpi: boolean; // Active la génération de BPI à la soumission
  generates_bed: boolean; // Active la chaîne paiement BED (solde) à la soumission
  created_at: string;
  updated_at: string;

  // Relations (chargées via Supabase select)
  origin?: {
    id: string;
    name: string;
    label: string;
  };
  product?: Product;
  fields?: FormFieldWithValues[];
  submissions_count?: number; // Count via aggregation
}

/**
 * Soumission d'un formulaire
 */
export interface FormSubmission {
  id: string;
  form_id: string;
  contact_id?: string;
  data: Record<string, any>; // { field_id: value }
  bpi_code?: string; // Code unique du Bulletin de Pré-Inscription (format: BPI-XXXXXX)
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  converted_at?: string; // Date de conversion (assignation à un équipage). NULL = prospect actif.
  payment_status?: string; // Statut BPI depuis payment_cases (via prospects_view)

  // Relations
  form?: Form;
  contact?: Contact;
}

// =====================================================
// DTOs (Data Transfer Objects)
// =====================================================

/**
 * Payload pour créer un formulaire
 */
export interface CreateFormDto {
  name: string;
  slug?: string; // Optionnel, auto-généré si non fourni
  product_id?: string;
  origin_id?: string;
  is_active?: boolean;
  form_type?: FormType; // Type de formulaire (défaut: 'simple')
  generates_bpi?: boolean; // Active la génération de BPI (défaut: false)
  generates_bed?: boolean; // Active la chaîne paiement BED (défaut: false)
}

/**
 * Payload pour mettre à jour un formulaire
 */
export interface UpdateFormDto {
  name?: string;
  slug?: string;
  product_id?: string;
  origin_id?: string;
  is_active?: boolean;
  form_type?: FormType;
  generates_bpi?: boolean;
  generates_bed?: boolean;
}

/**
 * Payload pour créer un champ (association template)
 */
export interface CreateFormFieldDto {
  form_id: string;
  field_template_id: string;
  order_index: number;

  // Overrides optionnels
  label?: string;
  placeholder?: string;
  is_required?: boolean;
  options?: string[];
}

/**
 * Payload pour mettre à jour un champ
 */
export interface UpdateFormFieldDto {
  order_index?: number;
  label?: string;
  placeholder?: string;
  is_required?: boolean;
  options?: string[];
}

/**
 * Payload pour créer une soumission de formulaire
 */
export interface CreateFormSubmissionDto {
  form_id: string;
  data: Record<string, any>;
  contact_id?: string;
}

// =====================================================
// HELPERS
// =====================================================

/**
 * Fusionne un FormField avec son template pour obtenir les valeurs effectives
 */
export function mergeFieldWithTemplate(field: FormField): FormFieldWithValues {
  const template = field.field_template;

  if (!template) {
    throw new Error(`Field ${field.id} has no template`);
  }

  return {
    id: field.id,
    form_id: field.form_id,
    field_template_id: field.field_template_id,
    order_index: field.order_index,

    // Utilise l'override si présent, sinon le template
    label: field.label ?? template.label,
    field_type: template.field_type,
    placeholder: field.placeholder ?? template.placeholder,
    is_required: field.is_required ?? template.is_required,
    options: field.options ?? template.options,
    contact_field_map: template.contact_field_map,
    target_role: template.target_role, // Rôle cible pour les formulaires duo
    bed_deferrable_group: template.bed_deferrable_group ?? null,

    created_at: field.created_at,
    updated_at: field.updated_at,
  };
}

/**
 * Génère un slug unique à partir d'un nom
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Enlever les accents
    .replace(/[^a-z0-9]+/g, '-') // Remplacer les caractères spéciaux par des tirets
    .replace(/^-+|-+$/g, ''); // Enlever les tirets en début/fin
}

/**
 * Génère l'URL publique d'un formulaire
 */
export function getPublicFormUrl(slug: string, baseUrl?: string): string {
  const base = baseUrl || window.location.origin;
  return `${base}/public/forms/${slug}`;
}

/**
 * Génère le code iframe pour un formulaire
 */
export function getFormIframeCode(slug: string, baseUrl?: string): string {
  const url = getPublicFormUrl(slug, baseUrl);
  return `<iframe src="${url}" width="100%" height="600" frameborder="0" style="border: 1px solid #e4e4e7; border-radius: 8px;"></iframe>`;
}

/**
 * Valide les données d'une soumission selon les champs du formulaire
 */
export function validateSubmissionData(
  data: Record<string, any>,
  fields: FormFieldWithValues[]
): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  fields.forEach((field) => {
    const value = data[field.id];

    // Vérifier les champs requis
    if (field.is_required && !value) {
      errors[field.id] = `${field.label} est requis`;
      return;
    }

    // Valider le type email
    if (field.field_type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        errors[field.id] = 'Email invalide';
      }
    }

    // Valider le type phone
    if (field.field_type === 'phone' && value) {
      const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
      if (!phoneRegex.test(value)) {
        errors[field.id] = 'Numéro de téléphone invalide';
      }
    }

    // Valider le type number
    if (field.field_type === 'number' && value) {
      if (isNaN(Number(value))) {
        errors[field.id] = 'Doit être un nombre';
      }
    }
  });

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}
